import { debounce } from 'tiny-throttle';
import { cloneButtons, isVisible } from './buttons';
import { logInfo, logError } from '../utils/logging';

const DRAWER_SELECTOR_LIST_BY_PRIORITY = [
  '#cart-notification',
  '#CartDrawer',
  '.cart-popup-wrapper',
  '#cart',
  '.mini-cart',
  '.cart',
  '.mini_cart',
  '.cart_container',
  '.cart-popup',
  '.drawer',
  '.cart_content',
  '.Drawer',
  '.cart-preview',
  '.cart-drawer-form',
  '.theme-ajax-cart',
  '.ajax-cart__form',
];

function cloneButtonsMutation(mutationsList) {
  for (const mutation of mutationsList) {
    if (mutation.type === 'childList') {
      for (const node of mutation.addedNodes) {
        if (
          node.querySelector &&
          node.querySelector(
            'form[action*="/cart"],form[action*="/checkout"],[type=submit]',
          )
        ) {
          cloneButtons();
        }

        const checkoutButton = document.querySelector(
          '[href$="checkout"]:not(.giftbox-checkout)',
        );
        if (checkoutButton && isVisible(checkoutButton)) {
          cloneButtons();
        }
      }
    }
  }
}

function enhancedCb() {
  const _cb = (mutationsList) => {
    for (const cb of _cb.enhancers) {
      cb(mutationsList);
    }
  };
  _cb.enhancers = [];
  _cb.inject = function (e) {
    this.enhancers.push(e);
  };
  return _cb;
}

function observeSelectors(selectors, cb, maxObserverCnt = 2) {
  const selectorsObservers = [];
  let counter = 0;
  for (const selector of selectors) {
    if (counter === maxObserverCnt) {
      break;
    }
    const { name = selector, value = selector } = selector;
    const element = document.querySelector(value);
    if (element) {
      const observer = hookMutationObserverToElement(element, value, name, cb);
      if (observer) {
        selectorsObservers.push(observer);
        counter++;
      }
    }
  }
  return selectorsObservers;
}

function disableObservers(observers) {
  for (const observer of observers) {
    if (observer) {
      observer.disconnect();
    }
  }
}

function observeForms(cb) {
  const forms = document.querySelectorAll("form[action^='/checkout']");
  const formObservers = [];
  for (let form of forms) {
    const formObserver = new MutationObserver(cb);
    formObserver.observe(form, {
      attributes: true,
    });
    formObservers.push(formObserver);
  }
  return formObservers;
}

function hookMutationObserverToElement(
  element,
  selector,
  selectorName = null,
  cb,
) {
  if (element) {
    logInfo(`Found ${selectorName ? selectorName : selector}: ${selector}`);
    const observer = new MutationObserver(cb);
    observer.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    return observer;
  }
}

const debouncedCloneButtons = debounce(cloneButtons, 500);
class CartWatcher {
  constructor() {
    this.updateObservers = this.updateObservers.bind(this);
    this.rootCallback = enhancedCb();
    this.drawerCallback = enhancedCb();
    this.rootCallback.inject(cloneButtonsMutation);
    this.drawerCallback.inject(debouncedCloneButtons);
  }

  setupDefaultObservers() {
    try {
      const targetNode = document.body;

      const observer = new MutationObserver(this.rootCallback);
      observer.observe(targetNode, { childList: true, subtree: true });

      observeForms(this.rootCallback);

      this.selectorObservers = observeSelectors(
        DRAWER_SELECTOR_LIST_BY_PRIORITY,
        this.drawerCallback,
      );
    } catch (error) {
      logError('Failed to setup drawer cart observer', error);
    }
  }

  updateObserversFromSettings(settings) {
    this.settings = settings;
    if (
      !settings.ajax_cart_watcher_selector ||
      !document.querySelector(settings.ajax_cart_watcher_selector)
    ) {
      return;
    }

    try {
      const backOfficeSelector = settings.ajax_cart_watcher_selector
        ? {
            name: 'ajax_cart_watcher_selector',
            value: settings.ajax_cart_watcher_selector,
          }
        : null;

      let selectors = DRAWER_SELECTOR_LIST_BY_PRIORITY;
      if (backOfficeSelector) {
        disableObservers(this.selectorObservers);
        selectors = [backOfficeSelector, ...DRAWER_SELECTOR_LIST_BY_PRIORITY];
      }

      observeSelectors(selectors, this.drawerCallback);
    } catch (error) {
      logError('Failed to setup drawer cart observer', error);
    }
  }

  updateObservers() {
    if (this.settings) {
      this.updateObserversFromSettings(this.settings);
    }
  }
}

const cartWatcher = new CartWatcher();
export default cartWatcher;

// Needed here because this file is used in admin too
window.GiftBox = window.GiftBox || {};
window.GiftBox.cloneButtons =
  window.GiftBox.cloneButtons || debouncedCloneButtons;
window.GiftBox.updateObservers =
  window.GiftBox.updateObservers || debounce(cartWatcher.updateObservers, 500);
window.GiftBox.drawerCallback =
  window.GiftBox.drawerCallback || cartWatcher.drawerCallback;
